import type { DataCompartment, IAppStorage } from '@aesop-fables/scrinium';
import {
  ConfiguredDataSource,
  DataCompartmentOptions,
  DataCache,
  createDataCache,
  createDataCacheModule,
} from '@aesop-fables/scrinium';
import type { IServiceContainer } from '@aesop-fables/containr';
import { PricebookBundle } from '../../models/PricebookData';
import { SelectionData } from '../../api/apis/PricebookApi';
import { derivePricebookSelections, isFreemiumPricebook } from '../../helpers/pricebookUtils';
import { PricebookCompartments, pricebookStorageKey } from '../pricebook';
import { PricebooksSelected } from '../../predicates/PricebooksSelected';

export interface ProductTypeCompartments {
  productType: DataCompartmentOptions<string>;
  paidBy: DataCompartmentOptions<string | undefined>;
}

export const productTypeStorageKey = 'data/productType';

const getPricebookSelections = (pricebookDataCache: DataCache<PricebookCompartments>) => {
  const pricebookOptions = (
    pricebookDataCache.findCompartment('pricebookOptions') as DataCompartment<PricebookBundle[]>
  ).getData() as PricebookBundle[];
  const pricebookSelectionIds = (
    pricebookDataCache.findCompartment('pricebookSelectionIds') as DataCompartment<SelectionData[]>
  ).getData() as SelectionData[];

  if (!pricebookOptions || !pricebookSelectionIds) return [];

  return derivePricebookSelections(pricebookOptions, pricebookSelectionIds);
};

export const withProductTypeData = createDataCacheModule(
  (appStorage: IAppStorage, container: IServiceContainer) => {
    const cache = createDataCache<ProductTypeCompartments>({
      productType: {
        loadingOptions: {
          strategy: 'auto',
          predicate: container.resolve(PricebooksSelected),
        },
        source: new ConfiguredDataSource(async () => {
          const pricebookDataCache =
            appStorage.retrieve<PricebookCompartments>(pricebookStorageKey);
          const pricebookSelections = getPricebookSelections(pricebookDataCache);
          if (pricebookSelections.length === 0) {
            return '';
          }
          return pricebookSelections[0].pricebook.productType;
        }),
        defaultValue: '',
      },
      paidBy: {
        loadingOptions: {
          strategy: 'auto',
          predicate: container.resolve(PricebooksSelected),
        },
        source: new ConfiguredDataSource(async () => {
          const pricebookDataCache =
            appStorage.retrieve<PricebookCompartments>(pricebookStorageKey);
          const pricebookSelections = getPricebookSelections(pricebookDataCache);

          const freemiumPricebook = pricebookSelections.find(pricebookBundle =>
            isFreemiumPricebook(pricebookBundle.pricebook),
          );
          if (pricebookSelections.length > 1 && freemiumPricebook) {
            // Freemium + Premium Add-on Case
            return freemiumPricebook.pricebook.paidBy;
          }
          if (pricebookSelections.length > 0) {
            return pricebookSelections[0]?.pricebook.paidBy;
          }
          return undefined;
        }),
        defaultValue: undefined,
      },
    });

    appStorage.store(productTypeStorageKey, cache);
  },
);
